import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { RichpanelInfo } from '../types';
import { RichpanelConnectModal } from './RichpanelConnectModal';
import { useRichPanel } from './useRichpanel';

export const RichpanelItem = (props: IntegrationItemProps) => {
  const { isConnector } = props;
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const {
    richpanelWorkspace,
    disconnectRichpanelMutation,
    requestImportMutation,
  } = useRichPanel(!isConnector);

  const connectModalState = useModalState();
  const importModalState = useModalState<{
    workspace: RichpanelInfo;
    isNew: boolean;
  }>();

  const handleConnectComplete = (workspace: RichpanelInfo) => {
    if (!currWorkspace) return;
    fetchFeedbackSources(currWorkspace.alias);
    connectModalState.close();
    importModalState.open({
      modalState: {
        workspace,
        isNew: true,
      },
    });
  };

  const handleDisconnect = () => {
    disconnectRichpanelMutation();
  };

  const handleImportWorkspace = async (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!richpanelWorkspace}
        source={SOURCE_TYPE.RICH_PANEL}
        description="Import conversations as feedbacks into Syncly."
        connectedDisplay="Richpanel workspace"
        info={richpanelWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => connectModalState.open()}
        additionalActions={(workspace: RichpanelInfo) => (
          <div>
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace: workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
          </div>
        )}
      />
      <Modal
        status={connectModalState.status}
        closeModal={connectModalState.close}
      >
        <RichpanelConnectModal onConnectComplete={handleConnectComplete} />
      </Modal>
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
      >
        <IntegrationImportModal
          isNew={importModalState.modalState?.isNew || false}
          title={'Import feedback'}
          subTitle={'Richpanel'}
          subTitleList={richpanelWorkspace?.alias}
          historyList={richpanelWorkspace?.history || []}
          onSubmitImport={(start_date, end_date) =>
            handleImportWorkspace({ start_date, end_date })
          }
        />
      </Modal>
    </>
  );
};
